import { FeedbackType } from '@/models/feedback.model';
import { showApiErrors } from '@/utils';
import { createSlice, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { TGetFeedbacksQuery } from '../helpers/types';
import { changeFeedbackStatus, deleteFeedback, getFeedbacks, getNewFeedbacksCount } from './actionCreators';
import {
  initialFeedbackListState,
  initialNewCountByType,
  initialState,
  FEEDBACKS_SLICE_NAME,
  FeedbacksListsState,
} from './models';

export const slice = createSlice({
  name: FEEDBACKS_SLICE_NAME,
  initialState,
  reducers: {
    resetFeedbacksState(state: FeedbacksListsState) {
      return {
        ...initialState,
        newCount: state.newCount,
        newCountByType: state.newCountByType,
        isNewCountLoading: state.isNewCountLoading,
      };
    },
    resetFeedbackList: (state: FeedbacksListsState, action: PayloadAction<FeedbackType>) => {
      state[action.payload] = { ...initialFeedbackListState };
    },
    changeQuery: (state: FeedbacksListsState, action: PayloadAction<TGetFeedbacksQuery>) => {
      state.query = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFeedbacks.fulfilled, (state: FeedbacksListsState, action) => {
        if (!action.meta.arg.type) return;
        state[action.meta.arg.type] = {
          error: null,
          isLoading: false,
          list: action.payload.data,
          pagination: action.payload.meta,
        };
      })
      .addCase(changeFeedbackStatus.fulfilled, (state: FeedbacksListsState, action) => {
        if (!action.meta.arg.type) return;
        state[action.meta.arg.type] = {
          error: null,
          isLoading: false,
          list: (state[action.meta.arg.type].list ?? []).map((feedback) => {
            if (feedback.id === action.meta.arg.id) {
              return { ...feedback, status: action.meta.arg.status };
            }
            return feedback;
          }),
        };
      })
      .addCase(getNewFeedbacksCount.pending, (state: FeedbacksListsState) => {
        state.isNewCountLoading = true;
      })
      .addCase(getNewFeedbacksCount.fulfilled, (state: FeedbacksListsState, action) => {
        state.isNewCountLoading = false;
        state.newCount = action.payload.data.count;
        state.newCountByType = {
          ...initialNewCountByType,
          ...(action.payload.data.by_type ?? {}),
        };
      })
      .addCase(getNewFeedbacksCount.rejected, (state: FeedbacksListsState) => {
        state.isNewCountLoading = false;
      })
      .addMatcher(
        isPending(changeFeedbackStatus, deleteFeedback, getFeedbacks),
        (state: FeedbacksListsState, action) => {
          if (!action.meta.arg.type) return;
          state[action.meta.arg.type].isLoading = true;
          state[action.meta.arg.type].error = null;
        },
      )
      .addMatcher(
        isRejected(changeFeedbackStatus, deleteFeedback, getFeedbacks),
        (state: FeedbacksListsState, action) => {
          const { error } = action;
          if (!action.meta.arg.type) return;

          state[action.meta.arg.type].isLoading = false;
          state[action.meta.arg.type].error = error;

          showApiErrors(error);
        },
      );
  },
});

export const { resetFeedbacksState, resetFeedbackList, changeQuery } = slice.actions;

export default slice.reducer;
